/**
 * BarAnimationOverlay - Cinematic "The Saloon Bar Slide" Animation Overlay
 *
 * Displays a theatrical tavern overlay featuring wooden saloon swinging doors kicking open,
 * a polished counter sliding in, an overflowing frothy beer mug sliding across the bar and
 * bumping the player's card, frantic chugging, spinning spiral eyes, procedural rubber-noodle
 * wobbly IK legs, and a face-first collapse onto a bar stool while soap bubbles pop.
 */

let activeBarOverlayInstance = null;

export class BarAnimationOverlay {
  /**
   * Displays the Saloon Bar Slide animation in an interactive overlay.
   *
   * @param {Object} [options]
   * @param {string} [options.playerName] - Name of player at the bar (e.g. "You", "Computer")
   * @param {string} [options.who] - Seat ID ('p1', 'p2', etc.)
   * @param {number} [options.barHole] - The board hole number of the bar/drink space
   * @param {Object} [options.card] - Card object { r, s } representing the player's card
   * @param {Object} [options.settings] - Game settings object
   * @param {boolean} [options.isDrinkSpace] - True if triggered by a Pints & Pegs drinking tile
   * @param {Function} [options.onComplete] - Callback on dismiss or completion
   * @returns {BarAnimationOverlay|null}
   */
  static show(options = {}) {
    const settings = options.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings && (settings.barAnimation === 'no' || settings.barAnimation === 'off' || settings.barAnimation === false)) {
      if (typeof options.onComplete === 'function') options.onComplete();
      return null;
    }

    if (typeof document === 'undefined') {
      if (typeof options.onComplete === 'function') options.onComplete();
      return null;
    }

    if (activeBarOverlayInstance) {
      activeBarOverlayInstance.close(false);
    }

    const instance = new BarAnimationOverlay(options);
    activeBarOverlayInstance = instance;
    instance.mount();
    return instance;
  }

  static hide() {
    if (activeBarOverlayInstance) {
      activeBarOverlayInstance.close(true);
    }
  }

  constructor(options = {}) {
    this.options = options;
    this.onComplete = options.onComplete || (() => {});
    this.overlayEl = null;
    this.autoCloseTimer = null;
    this.bubbleTimer = null;
    this.stepTimers = [];
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  formatCard(card, fallbackRank = 'J', fallbackSuit = '♠') {
    if (!card) {
      return { r: fallbackRank, s: fallbackSuit, isRed: fallbackSuit === '♥' || fallbackSuit === '♦' };
    }
    if (Array.isArray(card) && card.length > 0) {
      card = card[card.length - 1];
    }
    if (typeof card === 'string') {
      const match = card.match(/^([0-9AJQK]+)([HDSC♥♦♠♣]?)$/i);
      if (match) {
        card = { r: match[1], s: match[2] };
      }
    }
    let r = card.r !== undefined ? card.r : (card.rank !== undefined ? card.rank : fallbackRank);
    if (r === 1 || r === '1') r = 'A';
    else if (r === 11 || r === '11') r = 'J';
    else if (r === 12 || r === '12') r = 'Q';
    else if (r === 13 || r === '13') r = 'K';
    r = String(r).toUpperCase();

    let s = card.s || card.suit || fallbackSuit;
    if (s === 'H' || s === 'h') s = '♥';
    else if (s === 'D' || s === 'd') s = '♦';
    else if (s === 'S' || s === 's') s = '♠';
    else if (s === 'C' || s === 'c') s = '♣';
    const isRed = s === '♥' || s === '♦';
    return { r, s, isRed };
  }

  buildHtml() {
    const playerName = this.options.playerName || 'Player';
    const barHole = this.options.barHole !== undefined ? this.options.barHole : 40;
    const isDrink = Boolean(this.options.isDrinkSpace);
    const card = this.formatCard(this.options.card, 'J', '♠');

    const badgeText = isDrink ? '🍻 DRINK SPACE! SOCIAL CALL' : '🍺 THE BAR TRAP! TIME TO CHUG';
    const titleText = isDrink ? `${playerName} Takes a Drink!` : `${playerName} Pulled Up to The Bar!`;
    const subText = isDrink ? `Landed on Drinking Space at Hole ${barHole} — Bottoms up!` : `Landed on Bar Hole ${barHole} — Serving a 3-turn sentence!`;

    return `
      <div class="tavern-ambient-glow"></div>
      <button class="bar-skip-btn" id="barSkipBtn" type="button" title="Skip animation (Esc)">Skip ✕</button>

      <div class="bar-banner">
        <span class="bar-badge">${badgeText}</span>
        <h2 class="bar-title">${titleText}</h2>
        <p class="bar-subtitle">${subText}</p>
      </div>

      <div class="bar-stage" id="barStage">
        <!-- Saloon Back Wall with Shelves & Bottles -->
        <div class="saloon-backwall">
          <div class="bottle-shelf">
            <div class="bottles-row">
              <div class="bottle amber"></div>
              <div class="bottle green"></div>
              <div class="bottle brown"></div>
              <div class="bottle amber"></div>
              <div class="bottle green"></div>
              <div class="bottle brown"></div>
              <div class="bottle amber"></div>
            </div>
          </div>
        </div>

        <!-- 3D Saloon Swinging Doors (Batwing Doors) -->
        <div class="saloon-door-wrap left">
          <div class="saloon-door" id="saloonDoorL">
            <div class="door-hinge top"></div>
            <div class="door-hinge bottom"></div>
            <div class="door-slat"></div>
            <div class="door-slat"></div>
            <div class="door-slat"></div>
            <div class="door-slat"></div>
            <div class="door-slat"></div>
            <div class="door-slat"></div>
          </div>
        </div>
        <div class="saloon-door-wrap right">
          <div class="saloon-door" id="saloonDoorR">
            <div class="door-hinge top"></div>
            <div class="door-hinge bottom"></div>
            <div class="door-slat"></div>
            <div class="door-slat"></div>
            <div class="door-slat"></div>
            <div class="door-slat"></div>
            <div class="door-slat"></div>
            <div class="door-slat"></div>
          </div>
        </div>

        <!-- Wooden Bar Counter Surface -->
        <div class="bar-counter-surface" id="barCounter">
          <div class="counter-top-rim"></div>
          <div class="brass-rail"></div>
        </div>

        <!-- Frothy Overflowing Beer Mug Stein -->
        <div class="beer-mug" id="beerMug">
          <div class="mug-handle"></div>
          <div class="mug-glass">
            <div class="beer-liquid" id="beerLiquid">
              <div class="fizz-bubble"></div>
              <div class="fizz-bubble"></div>
              <div class="fizz-bubble"></div>
            </div>
          </div>
          <div class="beer-foam">
            <div class="foam-bubble b1"></div>
            <div class="foam-bubble b2"></div>
            <div class="foam-bubble b3"></div>
            <div class="foam-drip d1"></div>
            <div class="foam-drip d2"></div>
          </div>
        </div>

        <!-- The Player's Card with Wobbly Noodle IK Legs -->
        <div class="player-card ${card.isRed ? 'red' : 'black'}" id="barPlayerCard">
          <div class="corner tl">
            <span class="r">${card.r}</span>
            <span class="s">${card.s}</span>
          </div>

          <div class="card-face-center">
            <span class="main-pip" id="barCardPip">${card.s}</span>
            <!-- Hypnotic Spinning Spiral Eyes -->
            <div class="drunken-eyes" id="drunkenEyes">
              <svg class="spiral-eye" viewBox="0 0 100 100">
                <path d="M 50,50 A 5,5 0 0,1 55,50 A 10,10 0 0,1 45,50 A 15,15 0 0,1 60,50 A 20,20 0 0,1 40,50 A 25,25 0 0,1 65,50 A 30,30 0 0,1 35,50 A 35,35 0 0,1 70,50"
                      fill="none" stroke="#dc2626" stroke-width="8" stroke-linecap="round" />
              </svg>
              <svg class="spiral-eye" viewBox="0 0 100 100">
                <path d="M 50,50 A 5,5 0 0,1 55,50 A 10,10 0 0,1 45,50 A 15,15 0 0,1 60,50 A 20,20 0 0,1 40,50 A 25,25 0 0,1 65,50 A 30,30 0 0,1 35,50 A 35,35 0 0,1 70,50"
                      fill="none" stroke="#dc2626" stroke-width="8" stroke-linecap="round" />
              </svg>
            </div>
            <!-- Rosy Drunken Cheeks -->
            <div class="drunk-cheeks">
              <div class="cheek"></div>
              <div class="cheek"></div>
            </div>
            <!-- Foamy Mustache -->
            <div class="foam-mustache" id="foamMustache"></div>
          </div>

          <div class="corner br">
            <span class="r">${card.r}</span>
            <span class="s">${card.s}</span>
          </div>

          <!-- Rubber Noodle IK Legs -->
          <div class="noodle-legs" id="noodleLegs">
            <div class="noodle-leg left" id="legLeft">
              <div class="segment thigh">
                <div class="segment shin">
                  <div class="foot"></div>
                </div>
              </div>
            </div>
            <div class="noodle-leg right" id="legRight">
              <div class="segment thigh">
                <div class="segment shin">
                  <div class="foot"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Classic Round Bar Stool -->
        <div class="bar-stool" id="barStool">
          <div class="stool-seat"></div>
          <div class="stool-legs">
            <div class="leg l"></div>
            <div class="leg r"></div>
            <div class="rung top"></div>
            <div class="rung bottom"></div>
          </div>
        </div>
      </div>
    `;
  }

  mount() {
    let el = document.getElementById('barAnimationOverlay');
    if (!el) {
      el = document.createElement('div');
      el.id = 'barAnimationOverlay';
      el.className = 'bar-animation-overlay';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'The Saloon Bar Slide Animation');
      document.body.appendChild(el);
    }

    this.overlayEl = el;
    el.innerHTML = this.buildHtml();

    const skipBtn = el.querySelector('#barSkipBtn');
    if (skipBtn) {
      skipBtn.addEventListener('click', () => this.close(true));
    }

    window.addEventListener('keydown', this.handleKeyDown);

    // Force layout flush before activating CSS transitions
    void el.offsetWidth;
    el.classList.add('active');

    this.startChoreography();
  }

  getAudio() {
    return (typeof window !== 'undefined' && (window.audio || (window.CC && window.CC.audio))) || null;
  }

  startChoreography() {
    const audio = this.getAudio();

    // 0.0s: Saloon doors kick open!
    if (audio && typeof audio.saloonDoorKick === 'function') {
      audio.saloonDoorKick();
    }

    // 0.4s: Beer mug slides across counter
    this.scheduleStep(450, () => {
      if (audio && typeof audio.barMugSlide === 'function') {
        audio.barMugSlide();
      }
    });

    // 1.1s: Mug bumps into card with splash
    this.scheduleStep(1150, () => {
      if (audio && typeof audio.glassClink === 'function') {
        audio.glassClink();
      }
      const card = this.overlayEl?.querySelector('#barPlayerCard');
      if (card) card.classList.add('is-bumped');

      this.spawnBeerSplash();
    });

    // 1.4s: Card chugs the beer, mug tilts up, foam mustache appears
    this.scheduleStep(1450, () => {
      const mug = this.overlayEl?.querySelector('#beerMug');
      const card = this.overlayEl?.querySelector('#barPlayerCard');
      if (mug) mug.classList.add('is-chugging');
      if (card) card.classList.add('has-foam');

      if (audio && typeof audio.beerGulp === 'function') {
        audio.beerGulp();
      }
    });

    // 2.4s: Drunken transformation: spiral eyes & flushed cheeks appear, hiccup
    this.scheduleStep(2450, () => {
      const card = this.overlayEl?.querySelector('#barPlayerCard');
      if (card) {
        card.classList.remove('is-bumped');
        card.classList.add('is-dizzy');
      }
      if (audio && typeof audio.drunkenHiccup === 'function') {
        audio.drunkenHiccup();
      }
    });

    // 2.6s: Noodle wobbly legs & drunken stagger side-to-side
    this.scheduleStep(2650, () => {
      const card = this.overlayEl?.querySelector('#barPlayerCard');
      if (card) card.classList.add('is-staggering');
    });

    // 3.4s: Collapse face-first onto bar stool & thud
    this.scheduleStep(3400, () => {
      const card = this.overlayEl?.querySelector('#barPlayerCard');
      if (card) {
        card.classList.remove('is-staggering');
        card.classList.add('is-collapsed');
      }
      if (audio && typeof audio.stoolCollapseThud === 'function') {
        audio.stoolCollapseThud();
      }

      this.spawnFloatingBubbles();
    });

    // 4.8s: Auto-close overlay gracefully
    this.autoCloseTimer = setTimeout(() => {
      this.close(true);
    }, 4850);
  }

  scheduleStep(delay, fn) {
    const t = setTimeout(fn, delay);
    this.stepTimers.push(t);
  }

  spawnBeerSplash() {
    const stage = this.overlayEl?.querySelector('#barStage');
    if (!stage) return;

    for (let i = 0; i < 7; i++) {
      const drop = document.createElement('div');
      drop.className = 'beer-splash animate';
      drop.style.left = '48%';
      drop.style.bottom = '195px';
      const angle = (Math.PI / 6) + (Math.random() * Math.PI * 0.7);
      const dist = 30 + Math.random() * 55;
      const tx = Math.cos(angle) * dist;
      const ty = -Math.sin(angle) * dist;
      drop.style.setProperty('--tx', `${tx}px`);
      drop.style.setProperty('--ty', `${ty}px`);
      stage.appendChild(drop);
      setTimeout(() => drop.remove(), 700);
    }
  }

  spawnFloatingBubbles() {
    const stage = this.overlayEl?.querySelector('#barStage');
    const audio = this.getAudio();
    if (!stage) return;

    const stool = stage.querySelector('#barStool');
    const stoolRect = stool ? stool.getBoundingClientRect() : null;
    const stageRect = stage.getBoundingClientRect();

    const baseX = stoolRect ? (stoolRect.left - stageRect.left + stoolRect.width * 0.5) : (stage.offsetWidth * 0.75);
    const baseY = 160;

    for (let i = 0; i < 8; i++) {
      setTimeout(() => {
        if (!this.overlayEl) return;
        const b = document.createElement('div');
        b.className = 'saloon-bubble';
        const sz = 16 + Math.random() * 22;
        b.style.width = `${sz}px`;
        b.style.height = `${sz}px`;
        b.style.left = `${baseX + (Math.random() * 60 - 30)}px`;
        b.style.bottom = `${baseY + (Math.random() * 30)}px`;
        b.style.setProperty('--bx', `${(Math.random() * 40 - 20)}px`);
        stage.appendChild(b);

        // Random popping
        const popDelay = 1200 + Math.random() * 1200;
        setTimeout(() => {
          if (b.parentNode) {
            b.classList.add('popped');
            if (audio && typeof audio.bubblePop === 'function') {
              audio.bubblePop();
            }
            setTimeout(() => b.remove(), 200);
          }
        }, popDelay);

        setTimeout(() => {
          if (b.parentNode) b.remove();
        }, 3000);
      }, i * 160);
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' || e.key === 'Esc' || e.key === 'Enter' || e.key === ' ') {
      this.close(true);
    }
  }

  close(triggerCallback = true) {
    window.removeEventListener('keydown', this.handleKeyDown);

    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    this.stepTimers.forEach(t => clearTimeout(t));
    this.stepTimers = [];

    if (this.overlayEl) {
      this.overlayEl.classList.remove('active');
      setTimeout(() => {
        if (this.overlayEl && this.overlayEl.parentNode) {
          this.overlayEl.parentNode.removeChild(this.overlayEl);
        }
        this.overlayEl = null;
      }, 320);
    }

    if (activeBarOverlayInstance === this) {
      activeBarOverlayInstance = null;
    }

    if (triggerCallback && typeof this.onComplete === 'function') {
      const cb = this.onComplete;
      this.onComplete = null;
      try {
        cb();
      } catch (err) {
        console.error('[BarAnimationOverlay] onComplete error:', err);
      }
    }
  }
}
